import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Platform } from "react-native";

import {
  type AppTheme,
  resolveTheme,
  type ThemePreference,
  themes,
} from "./theme";
import { readThemePreference, writeThemePreference } from "./themeStorage";
import { type ThemeName, tokens } from "./tokens";
import { useDeviceScheme } from "./useDeviceScheme";

export type ThemeContextValue = AppTheme & {
  resolved: ThemeName;
  preference: ThemePreference;
  /** Applies at once and is remembered on this device (FR-022). */
  setPreference: (preference: ThemePreference) => void;
};

export const ThemeContext = createContext<ThemeContextValue | null>(null);

type Props = {
  children: ReactNode;
  /** The starting preference; without it the remembered choice is read (tests may force one). */
  preference?: ThemePreference;
};

/**
 * Provides the resolved theme and the user's preference (R7, R8). On the web the remembered
 * choice is read synchronously; on Android nothing renders until it has loaded, which happens
 * while the splash screen is held.
 */
export function ThemeProvider({ children, preference: initial }: Props) {
  const deviceScheme = useDeviceScheme();
  const [state, setState] = useState<{
    preference: ThemePreference;
    loaded: boolean;
  }>(() => {
    if (initial) return { preference: initial, loaded: true };
    const stored = readThemePreference();
    return typeof stored === "string"
      ? { preference: stored, loaded: true }
      : { preference: "system", loaded: false };
  });

  useEffect(() => {
    if (state.loaded) return undefined;
    let cancelled = false;
    void Promise.resolve(readThemePreference()).then((stored) => {
      // A choice made before the read finished wins (loaded is already true then).
      if (!cancelled) setState({ preference: stored, loaded: true });
    });
    return () => {
      cancelled = true;
    };
  }, [state.loaded]);

  const setPreference = useCallback((preference: ThemePreference) => {
    setState({ preference, loaded: true });
    void writeThemePreference(preference);
  }, []);

  const resolved = resolveTheme(state.preference, deviceScheme);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    // Keeps the page behind the app, and native controls, in the resolved theme on the web.
    document.documentElement.style.colorScheme = resolved;
    document.documentElement.style.backgroundColor =
      tokens.color[resolved].background;
  }, [resolved]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...themes[resolved],
      resolved,
      preference: state.preference,
      setPreference,
    }),
    [resolved, state.preference, setPreference],
  );
  if (!state.loaded) return null;
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
